import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, DestroyRef, effect, ElementRef, inject, OnInit, signal, untracked, ViewChild } from '@angular/core';
import { RegionsInfo, SitesShortForm, SiteTanksLookup, SiteTanksResponse } from '../../../interfaces/regions';
import { CommonModule } from '@angular/common';
import { MatTable, MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatCardModule } from '@angular/material/card';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { LookUpService } from '../../../services/lookupservice';
import { NotificationService } from '../../../services/notificationservice';
import { FeedstockService } from '../../../services/feedstockservice';
import { forkJoin, Subject, switchMap, take, takeUntil } from 'rxjs';
import { SiteService } from '../../../services/siteservice';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { ScratchPadComponent } from './scratch-pad/scratch-pad.component';
import { DailyDosingTableResponse } from '../../../interfaces/daily-dosing';
import { FormArray, FormsModule } from '@angular/forms';
import { DailyDosingService } from '../../../services/daily-dosing.service';
import { MatMenuModule } from '@angular/material/menu';
import { AddEditDailyDosingComponent } from './add-edit-daily-dosing/add-edit-daily-dosing.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ColumnMeta } from '../../../models/dosing.model';
import { DynamicField } from '../../../interfaces/dynamic-form-model';
import { DynamicFormRuleApplierService } from '../../../services/dynamic-form-builder-rule-applier.service';
import { ConfirmationDialogComponent, ConfirmationDialogData } from '../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { RolePermissionsService } from '../../../services/role-permissions.service';
import { LoadScheduleControlFlags, ScreenName } from '../../../interfaces/securityPermissions';
import { formatDateTime } from '../../shared/commonutils/helper';
import { DatetimePickerInputComponent } from '../../shared/components/datetime-picker-input/datetime-picker-input.component';
import { RoleService } from '../../../services/role.service';
@Component({
  selector: 'app-daily-dosing',
  imports: [CommonModule, MatTableModule, MatButtonModule,
    MatIconModule, MatFormFieldModule, MatSelectModule,
    MatInputModule, MatCardModule, MatSortModule, MatDatepickerModule,
    DragDropModule, MatDialogModule, MatMenuModule, FormsModule, DatetimePickerInputComponent],
  templateUrl: './daily-dosing.component.html',
  styleUrl: './daily-dosing.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DailyDosingComponent implements OnInit {
  permissions = inject(RolePermissionsService);
  //Default Values
  readonly SCHEDULE_TYPE_ID = 1;
  isSalisburySite: boolean = false;
  salisburyDateTime: Date = new Date();
  // Dropdown state
  markets = signal<RegionsInfo[]>([]);
  selectedMarketId = signal<number>(-1);
  marketQuery = signal<string>('');

  disposalSites = signal<SitesShortForm[]>([]);
  selectedSiteId = signal<number | null>(0);
  siteQuery = signal<string>('');

  siteTanks = signal<SiteTanksLookup[]>([]);
  selectedSiteTankId = signal<number | null>(null);
  siteTankQuery = signal<string>('');

  //Date Signals
  selectedDate = signal<Date | null>(new Date());
  selectedTime = signal<string>(this.getCurrentTimeSlot(30));
  timeSlots = signal(this.generateTimeSlots(30));

  //Table
  columns: ColumnMeta[] = [];
  uiSchemaResponse: DynamicField[] = [];

  displayedColumns: string[] = [];         // keys for mat-table
  dataSource = new MatTableDataSource<any>;
  private table!: MatTable<any>;

  showTable: boolean = false;
  @ViewChild(MatSort) sort!: MatSort;

  private destroy$ = new Subject<void>();
  private destroyRef = inject(DestroyRef);
  controlPermissions: LoadScheduleControlFlags = new LoadScheduleControlFlags();

  constructor(private _lookupService: LookUpService,
    private _notificationService: NotificationService,
    private rolesService: RoleService,
    private _sitesService: SiteService,
    private _dialog: MatDialog,
    private _dailyDosingService: DailyDosingService,
    private _ruleApplier: DynamicFormRuleApplierService,
    private _cdr: ChangeDetectorRef
  ) {

    //Selected Market effect
    effect(() => {
      this.selectedSiteId.set(0);
      this.siteQuery.set('');
      this.loadDisposalSites();
    });

    //Selected Disposal Site Effect
    effect(() => {
      this.selectedSiteTankId.set(null);
      const siteId = this.selectedSiteId();
      if (!siteId || siteId <= 0) {
        this.siteTanks.set([]);
        this.selectedSiteTankId.set(null);
        return;
      }
      this.loadTanks(siteId);
    });

    //Tank/Site Selected Effect
    effect(() => {

      const k = siteIdAndTankIdKey();
      if (!k) {
        this.dataSource.data = [];
        this.siteTanks.set([]);
        this.selectedSiteTankId.set(null);
        return;
      }

      const [siteId, tankId] = k.split('|').map(Number);
      this.dataSource.data = [];
      this.getDailyDosingRecords(siteId, tankId);
    });

    //To restrict the duplicate calls,combined key 
    const siteIdAndTankIdKey = computed(() => {
      const siteId = this.selectedSiteId();
      const tankId = this.selectedSiteTankId();
      return (!siteId || siteId <= 0 || !tankId || tankId <= 0) ? null : `${siteId}|${tankId}`;
    });
  }

  @ViewChild(MatTable)
  set MatTable(table: MatTable<any>) {
    if (table) {
      this.table = table;
      this.table.renderRows();
    }
  }
  ngOnInit(): void {
  }

  // --- Market select handlers
  onMarketSelected(value: number): void {
    this.selectedMarketId.set(value);
  }

  onMarketQuery(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    this.marketQuery.set(searchTerm);
  }

  onSiteSelected(value: number): void {
    this.selectedSiteId.set(value);
    this.disposalSites().filter(s => s.siteName.toLowerCase().startsWith("salis"))
    let selectedSite = this.disposalSites().find(i => i.id == value);
    if(selectedSite?.siteName.toLowerCase().startsWith("salis")){
      this.isSalisburySite = true;
      this.salisburyDateTime = new Date();
    }else{
      this.isSalisburySite = false;
    }
  }

  onSiteQuery(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    this.siteQuery.set(searchTerm);
  }

  onSiteTankSelected(value: number): void {
    this.selectedSiteTankId.set(value)
  }

  onSiteTankQuery(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    this.siteTankQuery.set(searchTerm);
  }

  // Filtered views (case-insensitive)
  sitesFiltered = computed(() => {
    const q = this.siteQuery().trim().toLowerCase();
    return this.disposalSites().filter(s => s.siteName.toLowerCase().includes(q));
  });
  siteTanksFiltered = computed(() => {
    const q = this.siteTankQuery().trim().toLowerCase();
    return this.siteTanks().filter(s => s.name.toLowerCase().includes(q));
  });

  // --- trackBy helpers
  trackByMarketId = (_: number, item: RegionsInfo) => item.id;
  trackBySiteId = (_: number, item: SitesShortForm) => item.id;
  trackBySiteTankId = (_: number, item: SiteTanksLookup) => item.id;

  private getDailyDosingRecords(siteId: number, tankId: number) {
    forkJoin({
      uiSchema: this._lookupService.GetUISchema("DailyDosingInputs_Save"),
      apiResponse: this._dailyDosingService.getDailyDosingData(siteId, tankId)
    })
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(({ uiSchema, apiResponse }) => {

      //To apply the Change Detection here, otherwise grid columns not refreshing
      this.showTable = false;
      this._cdr.detectChanges();
      this.uiSchemaResponse = uiSchema;
      let ruleData = { "SiteID": this.selectedSiteId() }
      var columnsMetaAferRule = this._ruleApplier.applyRulesForRecords(this.uiSchemaResponse, ruleData);
      var removedHiddenColumns = columnsMetaAferRule
        .filter((x) => x.type !== 'hidden')
        .filter((c, i, arr) => arr.findIndex(x => x.key === c.key) === i);

      const siteName = this.disposalSites().find(s => s.id === siteId)?.siteName?.toLowerCase() ?? '';
      const tankName = this.siteTanks().find(t => t.id === tankId)?.name?.toLowerCase() ?? '';
      removedHiddenColumns = this.applyTankOverrides(removedHiddenColumns, siteName, tankName);

      this.columns = [...removedHiddenColumns];
      this.displayedColumns = ['actions', ...removedHiddenColumns.map(c => c.key)];
      this.dataSource.data = [...apiResponse]
      this.dataSource.sort = this.sort;
      //To apply the Change Detection here, otherwise grid columns not refreshing
      this.showTable = true
      this._cdr.detectChanges();
    });
  }

  private applyTankOverrides(columns: ColumnMeta[], siteName: string, tankName: string): ColumnMeta[] {
    const isHadley = siteName === 'hadley';
    
    let result = columns.map(col => {
      if (col.key === 'Other' && isHadley) {
        let header = 'Other';
        if (tankName === 'green')  header = 'Transfer Out';
        if (tankName === 'solids') header = 'Transfer In';
        return { ...col, header };
      }
      return col;
    });
    
    result = result.filter(c => c.key !== 'Heifer_Est');

    if (isHadley && tankName === 'solids') {
      const heiferSchema = this.uiSchemaResponse.find(s => s.key === 'Heifer_Est');
      if (heiferSchema) {
        const heiferCol: ColumnMeta = {
          key:    heiferSchema.key,
          header: heiferSchema.label,
          type:   heiferSchema.type,
          order: heiferSchema.order
          //visible: true
        };
        
        const insertIdx = result.findIndex(c => c.key === 'SetPoint_PumpOntime');
        if (insertIdx > -1) {
          result.splice(insertIdx, 0, heiferCol);
        } else {
          result.push(heiferCol);
        }
      }
    }
    return result;
  }
  
  applyTankSchemaOverrides(schema: DynamicField[], siteName: string, tankName: string): DynamicField[] {
    const isHadley = siteName === 'hadley';
    return schema.map(field => {
      if (field.key === 'Heifer_Est') {
        const shouldShow = isHadley && tankName === 'solids';
        return {
          ...field,
          visible: shouldShow,
          type: shouldShow ? 'number' : 'hidden'
        };
      }
        
      if (field.key === 'Other' && isHadley) {
        let label = 'Other';
        if (tankName === 'green')  label = 'Transfer Out';
        if (tankName === 'solids') label = 'Transfer In';
        return { ...field, label };
      }

      return field;
    });
  }

  private loadDisposalSites(): void {
    //this._lookupService.getADSitesByMarketId(-1, 1).pipe(takeUntil(this.destroy$)).subscribe({
    this._lookupService.getddSites().pipe(takeUntil(this.destroy$)).subscribe({
      next: (sites: SitesShortForm[]) => {
        console.log('Disposal Sites:', sites);
        this.disposalSites.set(sites);
        
        this.selectedSiteId.set(sites[0]?.id || 0);
      }
    });    
    
    let roles = this.rolesService.getRolesAsString();
    this.permissions.getRolePermissionsNew(roles, ScreenName.DailyDosing, 0).subscribe({
      next: (perms: LoadScheduleControlFlags) => {
        this.controlPermissions = perms;
      }
    });
  }  

  addSalisburyDosing(): void {
    const originalDate = this.salisburyDateTime;
    const month   = String(originalDate.getMonth() + 1).padStart(2, '0');
    const day     = String(originalDate.getDate()).padStart(2, '0');
    const year    = originalDate.getFullYear();
    let   hours   = originalDate.getHours();
    const minutes = String(originalDate.getMinutes()).padStart(2, '0');
    const ampm    = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    const hour12  = String(hours).padStart(2, '0');
    
    const dtToPass = `${month}/${day}/${year} ${hour12}:${minutes} ${ampm}`;
    this._dailyDosingService.addSalisburyDosing(dtToPass).pipe(takeUntil(this.destroy$)).subscribe({
      next: (resp: any) => {
        console.log("resp", resp);
        //this._notificationService.show('Salisbury dosing record successfully!', 'success');
        
        this.getDailyDosingRecords(this.selectedSiteId()!, this.selectedSiteTankId()!)
      }
    })
  }

  private loadTanks(siteId: number) {
    this._sitesService.getSiteTanksLookup(siteId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (siteTanks: SiteTanksLookup[]) => {
        this.siteTanks.set(siteTanks);
        if (siteTanks && siteTanks.length) {
          this.selectedSiteTankId.set(siteTanks[0].id);
          this.siteTankQuery.set('');
        }
      }
    })
  }

  //Combine datetime with AM/PM
  dateTime = computed(() => {

    const date = this.selectedDate();
    const time = this.selectedTime();

    //nothing selected
    if (!date) return null;

    //date only (time not selected)
    if (!time) {
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      return d;
    }

    //date+time
    const [timePart, meridian] = time.split(' ');
    let [hours, minutes] = timePart.split(':').map(Number);

    if (meridian === 'PM' && hours !== 12) {
      hours += 12;
    }
    if (meridian === 'AM' && hours === 12) {
      hours = 0;
    }
    const result = new Date(date);
    result.setHours(hours, minutes, 0, 0);
    return result;

  });

  private getCurrentTimeSlot(interval: number): string {
    const now = new Date();
    let minutes = Math.ceil(now.getMinutes() / interval) * interval;
    let hours = now.getHours();
    if (minutes == 60) {
      minutes = 0;
      hours++;
    }
    const ampm = hours >= 12 ? "PM" : "AM";
    const hour12 = hours % 12 == 0 ? 12 : hours % 12;
    return `${hour12.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${ampm}`
  }

  private generateTimeSlots(interval: number): string[] {
    const slots: string[] = [];
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += interval) {
        const hour12 = h % 12 == 0 ? 12 : h % 12;
        const ampm = h < 12 ? 'AM' : 'PM';
        slots.push(`${hour12.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')} ${ampm}`);
      }
    }
    return slots;
  }

  openAddForm() {
    const siteId = this.selectedSiteId();
    const tankId = this.selectedSiteTankId();

    if(siteId === 6){
      this.addSalisburyDosing();
      return;
    }

    if (!siteId || !tankId) {
      return;
    }
    const siteName = this.disposalSites().find(s => s.id === siteId)?.siteName?.toLowerCase() ?? '';
    const tankName = this.siteTanks().find(t => t.id === tankId)?.name?.toLowerCase() ?? '';
    let tank = this.siteTanks().find(x => x.id === tankId);
    const adjustedSchema = this.applyTankSchemaOverrides(this.uiSchemaResponse, siteName, tankName);
    var inputData = {
      columns: adjustedSchema,
      apiResponse: {
        ID: 0,
        SiteID: siteId,
        TankId: tankId,
        TankName: tank!.name,
        Datecaptured: new Date()
      },
      mode: 'add',
      canSave: true
    }

    const dialogRef = this._dialog.open(AddEditDailyDosingComponent, {
      width: '800px',
      maxWidth: '800px',
      maxHeight: '90vh',
      data: inputData,
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {      
      if(result){
        this.getDailyDosingRecords(this.selectedSiteId()!, this.selectedSiteTankId()!)
      }
    });
  }

  openEditForm(row: any) {
    const siteName = this.disposalSites().find(s => s.id === this.selectedSiteId())?.siteName?.toLowerCase() ?? '';
    const tankName = this.siteTanks().find(t => t.id === this.selectedSiteTankId())?.name?.toLowerCase() ?? '';
    forkJoin({
      apiResponse: this._dailyDosingService.getDailyDosingById(row.ID)
    })
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(({ apiResponse }) => {
      const adjustedSchema = this.applyTankSchemaOverrides(this.uiSchemaResponse, siteName, tankName);
      var inputData = {
        columns: adjustedSchema,
        apiResponse: apiResponse,
        mode: 'edit',
        canSave: this.controlPermissions.edit
      }

      const dialogRef = this._dialog.open(AddEditDailyDosingComponent, {
        width: '700px',
        maxWidth: '700px',
        maxHeight: '90vh',
        data: inputData,
        disableClose: true,
      });

      dialogRef.afterClosed().subscribe(result => {

        if(result){
        this.getDailyDosingRecords(this.selectedSiteId()!, this.selectedSiteTankId()!)
        }
      });
      
    });
  }

  openScratchPadPopup(row: any) {

    //Get the cratch Pad schema from API call and pass it
    forkJoin({
      uiSchema: this._lookupService.GetUISchema("DailyDosingInputs_Scratch-Pad"),
      apiResponse: this._dailyDosingService.getDailyDosingForScratchPad(row.ID)
    }).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ uiSchema,apiResponse }) => {
        const tankId = this.selectedSiteTankId();
        let tank = this.siteTanks().find(x => x.id === tankId);
        var inputData = {
          uiSchema: uiSchema, //ui schema from api
          apiResponse: apiResponse, // api response
          SiteID: this.selectedSiteId(),
          TankId: this.selectedSiteTankId(),
          TankName: tank?.name,
          ID: row.ID,
          canSave: this.controlPermissions.edit
        }
        const dialogRef = this._dialog.open(ScratchPadComponent, {
          data: inputData,
          disableClose: true,
          width: '85vw',       // 80% of viewport width
          maxWidth: '1200px',  // optional cap for large screens
          height: 'auto',      // let content define height

          panelClass: 'x-large-dialog'

        });

        dialogRef.afterClosed().subscribe(result => {

        });

    // this._dailyDosingService.getDailyDosingForScratchPad(row.ID).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
    //   next: (response: any[]) => {
    //     const tankId = this.selectedSiteTankId();
    //     let tank = this.siteTanks().find(x => x.id === tankId);
    //     var inputData = {
    //       columns: this.columns,  //filterd columns for grid
    //       uiSchema: this.uiSchemaResponse, //ui schema from api
    //       displayedColumns: this.displayedColumns, //grid displayed columns
    //       apiResponse: response, // api response
    //       SiteID: this.selectedSiteId(),
    //       TankId: this.selectedSiteTankId(),
    //       TankName: tank?.name,
    //       ID: row.ID
    //     }
    //     const dialogRef = this._dialog.open(ScratchPadComponent, {
    //       data: inputData,
    //       disableClose: true,
    //       width: '85vw',       // 80% of viewport width
    //       maxWidth: '1200px',  // optional cap for large screens
    //       height: 'auto',      // let content define height

    //       panelClass: 'x-large-dialog'

    //     });

    //     dialogRef.afterClosed().subscribe(result => {

    //     });
    //   }
   })
  }

  onDelete(row: any) {
    const dialogData: ConfirmationDialogData = {
      title: 'Confirm Deletion',
      message: `Are you sure you want to delete the dosing input?`,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: 'warn'
    };

    this._dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: dialogData,
      disableClose: true,
      panelClass: 'delete-confirmation-dialog-nowrap'
    }).afterClosed().subscribe(result => {
      if (result) {

        this._dailyDosingService.onDelete(row.ID).subscribe({
          next: (isSuccess: boolean) => {
            if (isSuccess) {
              this.getDailyDosingRecords(this.selectedSiteId()!, this.selectedSiteTankId()!);
            }
          }
        });

      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
